<template>
  <a v-if="book && !featured" :href="'/' + book.uid" class="featured-book-row compact-row">
    <div class="book-header">
      <h3 class="caret">{{ book.title }}</h3>
    </div>
  </a>
  <div v-else-if="book" class="featured-book-row">
    <div class="book-header" @click="open = !open">
      <h3 class="caret" :class="{ 'is-open': open }">{{ book.title }}</h3>
    </div>
    <transition name="expand">
      <div class="book-compact" v-show="open">

        <div class="book-cover">
          <a v-if="book.uid" :href="'/' + book.uid" tabindex="-1">
            <Image v-if="book.coverimages?.[0]?.url" :src="book.coverimages[0].url" :srcset="book.coverimages[0].srcset" sizes="22rem" :alt="book.title" />
            <div v-else class="cover-empty" />
          </a>
        </div>

        <div class="book-details">
          <p class="book-price" v-if="priceLabel">${{ priceLabel }}</p>
          <div class="book-specs" v-if="renderedIntro" v-html="renderedIntro" />
          <div class="book-actions">
            <button class="add-btn add-btn-sold-out" v-if="book.sold_out === 'true'" disabled>Sold Out</button>
            <button class="add-btn add-btn-sold-out" v-else-if="book.forthcoming === 'true'" disabled>Forthcoming</button>
            <button class="add-btn" v-else-if="priceLabel" @click.prevent.stop="addToCart">Add to Cart</button>
            <a v-if="book.uid" class="about-btn" :href="'/' + book.uid">Read more</a>
          </div>
        </div>

      </div>
    </transition>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import { useStore } from '../KQL.js'
  import { pageFromPath } from '../helpers.js'
  import Image from './Image.vue'
  import { useCartStore } from './CartStore.js'

  const kql = useStore();
  const cart = useCartStore();
  const props = defineProps(['uid', 'intro', 'featured', 'startOpen']);
  const open = ref(!!props.startOpen);

  const book = computed(() => props.uid ? pageFromPath(props.uid, kql.pages) : null);
  const renderedIntro = computed(() => {
    const raw = props.intro || book.value?.specs || '';
    return raw.replace(/\*(.+?)\*/g, '<em>$1</em>');
  });

  const priceLabel = computed(() => {
    const p = book.value?.price;
    if (!p || parseFloat(p) === 0) return null;
    return parseFloat(p).toFixed(2);
  });

  const addToCart = async () => {
    if (!book.value) return;
    const existing = cart.line_items.find(i => i._slug === book.value.uid);
    if (existing) {
      cart.updateQuantity(book.value.uid, existing.quantity + 1);
      cart.isOpen = true;
      cart.loadSession();
    } else {
      await cart.addItem(book.value);
    }
  };
</script>

<style scoped>
  .featured-book-row {
    border-top: 1px solid var(--border);
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .book-header {
    cursor: pointer;
    margin-bottom: 0.5rem;
  }

  h3 {
    padding-left: 0.75em;
    line-height: 1.1em;
    margin: 0;
    position: relative;
    font-size: 2.5rem;
  }

  h3.caret::before {
    position: absolute;
    left: 0;
    top: 0.35em;
    content: '\203a';
    display: inline-block;
    width: 0.5em;
    height: 0.5em;
    line-height: 0.15;
    text-align: center;
    transition: transform 0.2s ease;
    transform-origin: 50% 50%;
  }

  h3.caret.is-open::before {
    transform: rotate(90deg);
  }

  .book-header:hover h3.caret:not(.is-open)::before {
    transform: translateX(0.1em);
  }

  .expand-enter-active,
  .expand-leave-active {
    transition: opacity 0.3s ease;
    overflow: hidden;
  }

  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
  }

  .book-compact {
    display: flex;
    gap: 3rem;
    padding-top: 1rem;
    padding-bottom: 2rem;
    align-items: flex-start;
  }

  .book-cover {
    flex-shrink: 0;
    width: 22rem;
    height: 23rem;
    align-self: flex-start;
    overflow: hidden;
  }

  .book-cover a {
    display: block;
    width: 100%;
    height: 100%;
  }

  .book-cover a { opacity: 1 !important; }

  .book-cover img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    object-position: center top;
  }

  .cover-empty {
    width: 100%;
    aspect-ratio: 346 / 556;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 2px;
  }

  .book-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    min-width: 0;
    padding-top: 0.25rem;
  }

  .book-details {
    padding-top: 0;
  }

  .book-price {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.5rem;
    color: inherit;
    margin: 0;
  }

  .book-specs {
    font-family: "Copernicus", serif;
    font-size: 1.5rem;
    line-height: 1.45;
    color: inherit;
    max-width: 80rem;
    opacity: 0.85;
  }

  .book-actions {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  .add-btn,
  .about-btn {
    font-family: "Maison Neue Demi", sans-serif;
    font-size: 1.5rem;
    border-radius: 3em;
    padding: 0.55em 1.6em 0.6em;
    margin: 0;
    line-height: 1;
    display: inline-block;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease, opacity 0.18s;
    cursor: pointer;
    opacity: 1 !important;
  }

  .add-btn {
    background: var(--btn-solid-bg, #4a2f30);
    color: var(--btn-solid-text, #ebe0e1);
    border: 1px solid var(--btn-solid-bg, #4a2f30);
  }

  .add-btn:hover { background: var(--first, #4a2f30); color: var(--light); }
  .add-btn-sold-out { opacity: 0.45; cursor: default; }

  .about-btn {
    color: inherit;
    background: rgba(235, 224, 225, 0.35);
    border: none;
    text-decoration: none;
  }

  .about-btn:hover {
    background: var(--first, rgba(74, 47, 48, 1));
    color: var(--light);
  }

  @media (max-width: 600px) {
    .book-compact { flex-direction: column; gap: 2.5rem; align-items: flex-start; padding-left: 1.5em; }
    .book-cover { width: 60%; max-width: 22rem; height: auto; align-self: auto; }
    .book-cover img { height: auto; }
    h3 { font-size: 2rem; }
    .book-specs { font-size: 1.5rem; }
    .book-price { font-size: 1.5rem; }
    .book-title { font-size: 2.4rem; }
  }
</style>
